import MoveableHelper from "moveable-helper";
import { Frame } from "scenejs";
import { fitPoints, getAreaSize, getOverlapSize, isInside } from "overlap-area";

import _ from "lodash";
import { WidgetRegistry } from "../widgets/registry";
import { PAGE, ASSET, CAROUSEL, CAROUSEL_CARD, HOTSPOT, SECTION_COLUMN, SLIDE, LIVE_ELEMENT } from "constants/widgetTypes";

export default class MoveableData extends MoveableHelper {
  constructor(editorSession, eventBus) {
    super({
      createAuto: true,
      useBeforeRender: true,
    });

    this.editorSession = editorSession;
    this.eventBus = eventBus;
    this.containers = [];
    this.selectedTargets = []

    this.reparentTarget = null;
  }

  setSelectedTargets(targets) {
    this.selectedTargets = targets;
  }

  getSelectedTargets() {
    return this.selectedTargets;
  }

  addContainer(container) {
    this.removeContainer(container);
    this.containers.push(container);
  }

  removeContainer(container) {
    if (!container) {
      return;
    }

    this.containers = this.containers.filter(item => {
      return item.id != container.id;
    });
  }

  onWidgetDrag = (e) => {
    this.onDrag(e);
    this.hitTest(e);
  }

  onWidgetDragEnd = (e) => {
    const moveableManager = e.currentTarget;
    const { target } = moveableManager.state;

    const widget = target.dataset.widgetId;
    
    if (this.reparentTarget) {
      this.eventBus.trigger('onParentChange', {
        widget: widget,
        parent: this.reparentTarget
      });
    }

    const widgetType = target.dataset.widgetType;
    if(widgetType === LIVE_ELEMENT) {
      this.reparentTarget = null;
    }
  }

  hitTest = (e) => {
    if (_.size(this.containers) == 0) {
      return;
    }

    if(e.clientX === undefined && e.clientY === undefined){
      return;
    }

    const moveableManager = e.currentTarget;
    const { target, pos1, pos2, pos3, pos4, targetClientRect } = moveableManager.state;
    const points = fitPoints([pos1, pos2, pos4, pos3], targetClientRect);

    const widget = target.dataset.widgetId;
    const widgetType = target.dataset.widgetType;
    const widgetConfig = WidgetRegistry.moveableConfig(this.editorSession, widgetType); 

    const currentParent = target.closest(`[data-container="true"]`);
    const widgetRect = target.getBoundingClientRect();

    //get html dom elements at the current position 
    const elements = document.elementsFromPoint(e.clientX, e.clientY);
    const containers = Array.from(elements).map(el => {
      return el.closest(`[data-container="true"]`);
    }).filter(el => {
      if(!el){
        return false;
      }
      
      var canReparent = true;
      if(widgetConfig && widgetConfig.onReparent){
        canReparent = widgetConfig.onReparent(widget, el.id);
      }

      if(!canReparent){
        return false;
      }

      return true;
    });

    //check the list of containers and remove parent containers if child exists
    const processedContainers = [];
    containers.forEach(container => {
      var found = containers.find(item => {
        return item != container && container.contains(item);
      });

      if (!found && !processedContainers.includes(container)) {
        processedContainers.push(container);
      }
    });

    var request = {
      widget: widget
    }

    var targetParent = null;
    if (_.size(processedContainers) > 0) {
      targetParent = processedContainers[0];
    }

    if(targetParent){
      const clientRect = targetParent.getBoundingClientRect();
      const { left, top, width, height } = clientRect;
      const allowWidgetTypes = [PAGE, SLIDE, SECTION_COLUMN, widgetType !== CAROUSEL && CAROUSEL_CARD, widgetType !== CAROUSEL && HOTSPOT];

      let isAssetReparent;

      isAssetReparent = allowReparentCondition(allowWidgetTypes, widgetType, isAssetReparent, targetParent);


    if (currentParent?.id == targetParent.id) {

      const gridOutLeft = targetParent.querySelector('#gridOutLeft');
      const gridOutRight = targetParent.querySelector('#gridOutRight');

      const isIntersecting = (rect1, rect2) => {
          return !(rect2.left > rect1.right || 
                   rect2.right < rect1.left || 
                   rect2.top > rect1.bottom ||
                   rect2.bottom < rect1.top);
      };

      const gridOutLeftRect = gridOutLeft ? gridOutLeft.getBoundingClientRect() : null;
      const gridOutRightRect = gridOutRight ? gridOutRight.getBoundingClientRect() : null;

      const isHoveredOverGridOutLeft = gridOutLeftRect && isIntersecting(widgetRect, gridOutLeftRect);
      const isHoveredOverGridOutRight = gridOutRightRect && isIntersecting(widgetRect, gridOutRightRect);

      if (isHoveredOverGridOutLeft || isHoveredOverGridOutRight) {
                  
          request = {
            ...request,
            parent: targetParent,
            rect: {
              top, left, width, height
            },
            gridOut: true
          }
          this.eventBus.trigger('onParentHover', request);
      } else {
        this.eventBus.trigger('onParentChange', {
          widget: widget,
          parent: this.reparentTarget
        })
      }
      
    }
         
      if (this.reparentTarget && this.reparentTarget.id === targetParent.id) {
        //No need to retrigger 
        return;
      }

      if (currentParent?.id != targetParent.id && isAssetReparent) {
        request = {
          ...request,
          parent: targetParent,
          rect: {
            top, left, width, height
          }
        }
      }
    }

    this.reparentTarget = request.parent;
    this.eventBus.trigger('onParentHover', request);
  }

  onResize = (e) => {
    const { direction } = e;
    if (direction[0] != 0 && direction[1] != 0) {

      const target = e.target;
      const rect = target.getBoundingClientRect();
      const frame = this.getFrame(target);

      const width = parseFloat(rect.width);
      const height = parseFloat(rect.height);
      const ratio = width / height;

      frame.set("width", `${e.width}px`);
      frame.set("height", `${e.width / ratio}px`);

      this.testDrag(e.drag);
    } else {
      this.testResize(e);
    }

    this.testRender(e.target);
  }

  testDrag(e) {
    const target = e.target;
    const translate = e.translate;

    const frame = this.getFrame(target);
    const tx = `${translate[0]}px`;
    const ty = `${translate[1]}px`;

    if (frame.has("transform", "translate")) {
      frame.set("transform", "translate", `${tx},${ty}`);
    } else {
      frame.set("transform", "translateX", tx);
      frame.set("transform", "translateY", ty);
    }

    const widgetId = e.target.dataset.widgetId;
    const type = e.target.dataset.widgetType;
    const widgetConfig = WidgetRegistry.moveableConfig(this.editorSession, type);

    if (widgetConfig && widgetConfig.onDrag) {
      var layout = frame.get();
      layout = widgetConfig.onDrag(layout, widgetId);
      const newFrame = new Frame(layout);
      this.setFrame(target, newFrame);
    }
  }
}


function allowReparentCondition(allowWidgetTypes, widgetType, isAssetReparent, targetParent) {
  function isWidgetTypeAllowed(widgetType) {
    return allowWidgetTypes.includes(widgetType);
  }

  if (widgetType === ASSET || widgetType === CAROUSEL) {
    isAssetReparent = (
      isWidgetTypeAllowed(targetParent.getAttribute('data-widget-type'))
    );
  } else {
    isAssetReparent = true;
  }
  return isAssetReparent;
}

